/**
 * Fetch con retry e backoff esponenziale per gestire 429 (Too Many Requests).
 * Rispetta l'header Retry-After se presente, altrimenti scala esponenzialmente.
 */
async function fetchWithRetry(
	url: string,
	maxRetries = 3,
	baseDelay = 1000
): Promise<Response> {
	let lastError: Error | null = null;
	for (let attempt = 0; attempt <= maxRetries; attempt++) {
		const response = await fetch(url);
		if (response.status !== 429) return response;

		lastError = new Error(`HTTP 429 on attempt ${attempt + 1}`);
		if (attempt === maxRetries) return response;

		const retryAfter = response.headers.get("Retry-After");
		const delay = retryAfter
			? Math.min(Number(retryAfter) * 1000, 10000)
			: baseDelay * Math.pow(2, attempt);
		console.warn(`[fetchWithRetry] 429 on ${url}, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
		await new Promise(r => setTimeout(r, delay));
	}
	throw lastError ?? new Error("fetchWithRetry exhausted retries");
}

/**
 * Carica HTML di una section in contesto prodotto.
 * Se `plpCollectionHandle` è valorizzato, usa
 * `/collections/{handle}/products/{productHandle}?section_id=...`
 * così Liquid ha `collection` + `product` (badge da collection metafields, ecc.).
 * Solo `/products/...` non espone `collection` e i query param custom spesso non arrivano a Liquid.
 */
export async function loadProductSection(
	sectionId: string,
	productHandle: string,
	extraParams?: Record<string, string | boolean>,
	plpCollectionHandle?: string | null
): Promise<string> {
	const root = (window as any)?.Shopify?.routes?.root || "/";
	const normalizedRoot = root.endsWith("/") ? root : `${root}/`;
	const ph = encodeURIComponent(productHandle);
	const ch = plpCollectionHandle != null && String(plpCollectionHandle).trim() !== ""
		? encodeURIComponent(String(plpCollectionHandle).trim())
		: "";
	const path = ch ? `collections/${ch}/products/${ph}` : `products/${ph}`;
	const url = new URL(`${normalizedRoot}${path}`, window.location.origin);
	url.searchParams.set("section_id", sectionId);
	if (extraParams) {
		for (const [key, val] of Object.entries(extraParams)) {
			if (ch && key === "collection_handle") continue;
			url.searchParams.set(key, String(val));
		}
	}
	const response = await fetchWithRetry(url.toString());
	if (!response.ok) {
		throw new Error(`Failed to load product section (${response.status}) for ${productHandle} -> ${url.toString()}`);
	}
	const data = await response.text();
	if (!data || data.trim().length === 0) {
		throw new Error(`Empty product section response for ${productHandle} -> ${url.toString()}`);
	}
	return data;
}

/** Pausa tra batch di fetch per evitare di saturare il rate limit Shopify. */
export function batchDelay(ms = 100): Promise<void> {
	return new Promise(r => setTimeout(r, ms));
}

/**
 * Override puntuali per attributi che nell'indice _digital hanno un path
 * completamente diverso (non basta strippare "meta."). Se il pattern è
 * semplicemente meta.X.Y → X.Y, usa l'opzione stripMetaPrefix al posto
 * di aggiungere entries qui.
 */
export const DIGITAL_INDEX_ATTRIBUTE_OVERRIDES: Record<string, string> = {
};

// Trasforma URLSearchParams (location.search) in una stringa filters per Algolia
export function shopifyFiltersToAlgoliaFilters(
  params: URLSearchParams,
  opts?: {
    collectionHandle?: string;
    /** Mappa attributo-default → attributo-effettivo nell'indice target */
    attributeOverrides?: Record<string, string>;
    /**
     * Se true, i metafield usano direttamente <namespace>.<key> invece di
     * meta.<namespace>.<key>. Gli indici _digital della custom integration
     * indicizzano i metafield senza il prefisso "meta.".
     */
    stripMetaPrefix?: boolean;
  }
): string[] {
  const clauses: string[] = [];

  // raccogli tutte le chiavi 'filter.*'
  const keys = Array.from(params.keys()).filter(k => k.startsWith("filter."));

  // per evitare doppioni, lavoriamo per chiave unica
  const uniqKeys = Array.from(new Set(keys));

  for (const key of uniqKeys) {
    const valuesRaw = [...new Set(
      params.getAll(key)
        .flatMap(v => v.split(","))
        .map(v => v.trim())
        .filter(Boolean)
    )];

    if (!valuesRaw.length) continue;

    const parts = key.split("."); // es. ["filter","v","option","taglia"] o ["filter","v","price","gte"]
    const scope = parts[1];       // "p" | "v"
    const attr = parts[2];

    // helper per OR tra più valori
    const orGroup = (exprs: string[]) =>
      exprs.length > 1 ? `(${exprs.join(" OR ")})` : exprs[0];

    switch (attr) {
      // --- Product facets ---
      case "vendor": {
        const exprs = valuesRaw.map(v => `vendor:'${escapeAlgoliaValue(v)}'`);
        clauses.push(orGroup(exprs));
        break;
      }
      case "product_type": {
        const exprs = valuesRaw.map(v => `product_type:'${escapeAlgoliaValue(v)}'`);
        clauses.push(orGroup(exprs));
        break;
      }
      case "tag": { // Shopify -> Algolia spesso 'tags'
        const exprs = valuesRaw.map(v => `tags:'${escapeAlgoliaValue(v)}'`);
        clauses.push(orGroup(exprs));
        break;
      }

      // --- Variant option ---
      case "option": {
        // filter.v.option.<name>=val1,val2
        const optionName = parts[3]; // es. "taglia"
        if (!optionName) break;
        const exprs = valuesRaw.map(v =>
          `options.${optionName}:'${escapeAlgoliaValue(v)}'`
        );
        clauses.push(orGroup(exprs));
        break;
      }

      // --- Price range (numeric) ---
      case "price": {
        const op = parts[3]; // "gte" | "lte" | "gt" | "lt" ...
        for (const v of valuesRaw) {
          const num = Number(v.replace(",", "."));
          if (!Number.isFinite(num)) continue;
          const cmp =
            op === "gte" ? ">=" :
              op === "lte" ? "<=" :
                op === "gt" ? ">" :
                  op === "lt" ? "<" : undefined;
          if (cmp) clauses.push(`price ${cmp} ${num}`);
        }
        break;
      }

      // --- Metafields: filter.p.m.<ns>.<key>=val ---
      case "m": {
        const ns = parts[3];
        const mkey = parts[4];
        if (!ns || !mkey) break;
        const fullAttr = `meta.${ns}.${mkey}`;
        const defaultAttr = opts?.stripMetaPrefix ? `${ns}.${mkey}` : fullAttr;
        const algoliaAttr = opts?.attributeOverrides?.[fullAttr] ?? defaultAttr;
        const exprs = valuesRaw.map(v => {
          // I metafield booleani di Shopify sono indicizzati in Algolia come
          // numeri (1/0) o booleani nativi. La sintassi facet `attr:'true'`
          // o `attr:true` cerca la stringa "true" e non matcha mai.
          // Si usa la comparazione numerica: attr = 1 / attr = 0.
          if (v === 'true' || v === '1') {
            return `${algoliaAttr} = 1`;
          }
          if (v === 'false' || v === '0') {
            return `${algoliaAttr} = 0`;
          }
          if (ns === "custom" && mkey === "sesso") {
            return expandSessoFacetOrExpr(algoliaAttr, v);
          }
          return `${algoliaAttr}:'${escapeAlgoliaValue(v)}'`;
        });
        clauses.push(orGroup(exprs));
        break;
      }

      default: {
        const name = parts.slice(2).join(".");
        console.warn(`[Algolia] filtro non mappato esplicitamente: ${key} → ${name}`, valuesRaw);
        const exprs = valuesRaw.map(v =>
          isNumeric(v) ? `${name} = ${Number(v)}` : `${name}:'${escapeAlgoliaValue(v)}'`
        );
        clauses.push(orGroup(exprs));
        break;
      }
    }
  }

  return clauses;
}

function escapeAlgoliaValue(v: string) {
  // sfugge gli apici per l'uso tra singoli apici
  return v.replace(/'/g, "\\'");
}
function isNumeric(v: string) {
  return v.trim() !== "" && Number.isFinite(Number(v.replace(",", ".")));
}

/** Sinonimi di mercato per facet `sesso` in Algolia (allineato a punti-misura-lookup.liquid). */
const SESSO_SYNONYM_GROUPS: readonly (readonly string[])[] = [
  ["Donna", "donna", "femme", "woman", "women", "frau", "mujer", "female"],
  ["Uomo", "uomo", "homme", "man", "men", "herr", "hombre", "male"],
  ["Unisex", "unisex", "unisexe"],
  ["Bambino", "bambino"],
  ["Bambina", "bambina"],
] as const;

function sessoSynonymGroupFor(raw: string): readonly string[] | null {
  const lower = raw.trim().toLowerCase();
  for (const group of SESSO_SYNONYM_GROUPS) {
    if (group.some((g) => g.toLowerCase() === lower)) return group;
  }
  return null;
}

/**
 * Espande il valore filtro sesso verso varianti di casing e sinonimi di mercato,
 * così il facet Algolia matcha anche se l'indice ha "UOMO" / "Homme" ecc.
 */
function expandSessoFacetOrExpr(algoliaAttr: string, raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return `${algoliaAttr}:''`;
  }
  const variants = new Set<string>();
  variants.add(trimmed);
  const group = sessoSynonymGroupFor(trimmed);
  if (group) {
    const italian = group[0];
    variants.add(italian);
    variants.add(italian.toUpperCase());
    variants.add(italian.toLowerCase());
    for (const token of group) {
      variants.add(token);
      variants.add(token.charAt(0).toUpperCase() + token.slice(1).toLowerCase());
      variants.add(token.toUpperCase());
      variants.add(token.toLowerCase());
    }
  }
  const exprs = [...variants].map((v) => `${algoliaAttr}:'${escapeAlgoliaValue(v)}'`);
  return exprs.length > 1 ? `(${exprs.join(" OR ")})` : exprs[0];
}
